import { useQuery } from "@tanstack/react-query"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { isNil } from "lopermut"
import { ClockIcon } from "lucide-react"
import { shopifyCollectionsQuery } from "modules/settings/lib/shopify/shopifyCollectionsQuery"
import { shopifyProductTagsQuery } from "modules/settings/lib/shopify/shopifyProductTagsQuery"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
  SEARCH_CATALOG_MAX_BOOST_ENTRIES,
  SearchCatalogAvailabilityFilter,
  SearchCatalogBoost,
  SearchCatalogBoostEntryType,
  SearchCatalogCapabilityConfigSchema,
  SearchCatalogEntityIndexingMode,
  SearchCatalogProvider,
  SearchCatalogUrlType,
  SearchCatalogUtmParams,
  SearchCatalogVisibilityMode,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useShopifyVariantOptionsQuery } from "../../hooks/useShopifyVariantOptionsQuery"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CatalogBoostList } from "./CatalogBoostList"
import { CatalogMultiSelect } from "./CatalogMultiSelect"

const ProductSelectionConfigSchema = SearchCatalogCapabilityConfigSchema.pick({
  visibility: true,
  boost: true,
  availableTags: true,
  availableCollections: true,
})

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    provider?: string
    utmParams?: Partial<SearchCatalogUtmParams>
    entityIndexingMode?: string
    groupVariantsBy?: string | null
    availabilityFilter?: string
    urlType?: string
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

type SearchCatalogUtmFormParams = {
  source: string
  medium: string
  campaign: string
  term: string
  content: string
}

function normalizeEntityIndexingMode(value?: string) {
  if (value === "product_and_variant") {
    return SearchCatalogEntityIndexingMode.Variant
  }

  return value ?? SearchCatalogEntityIndexingMode.Product
}

type PendingSearchBehaviorChange = {
  entityIndexingMode: string
  groupVariantsBy: string | null
}

function mergeSelectedOptions(
  options: { value: string; label: string }[],
  selectedValues: string[],
) {
  const knownValues = new Set(options.map((option) => option.value))
  const missing = selectedValues.filter((value) => !knownValues.has(value))

  return [...options, ...missing.map((value) => ({ value, label: value }))]
}

function findVariantOption(options: string[], value?: string | null) {
  if (!value) return null

  const normalizedValue = value.trim().toLowerCase()
  return options.find((option) => option.toLowerCase() === normalizedValue) ?? null
}

function areVariantOptionsEqual(left?: string | null, right?: string | null) {
  const normalize = (value?: string | null) => {
    const trimmed = value?.trim()
    return trimmed ? trimmed.toLowerCase() : null
  }

  return normalize(left) === normalize(right)
}

export function SearchCatalogForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  const { provider, setProvider, providerOptions, isIntegrationsStatusPending } =
    useCapabilityProvider({
      capability,
      mode,
      initialConfig,
    })

  const [utmParams, setUtmParams] = useState<SearchCatalogUtmFormParams>({
    source: "permut",
    medium: "{{ conversation.channel }}",
    campaign: "{{ conversation.campaign.name }}",
    term: initialConfig?.utmParams?.term ?? "",
    content: initialConfig?.utmParams?.content ?? "",
  })
  const [availabilityFilter, setAvailabilityFilter] = useState(
    initialConfig?.availabilityFilter ?? SearchCatalogAvailabilityFilter.Available,
  )
  const [urlType, setUrlType] = useState(initialConfig?.urlType ?? SearchCatalogUrlType.Checkout)
  const persistedEntityIndexingMode = normalizeEntityIndexingMode(initialConfig?.entityIndexingMode)
  const [entityIndexingMode, setEntityIndexingMode] = useState(persistedEntityIndexingMode)
  const persistedGroupVariantsBy = initialConfig?.groupVariantsBy ?? null
  const [groupVariantsBy, setGroupVariantsBy] = useState<string | null>(persistedGroupVariantsBy)
  const [pendingSearchBehaviorChange, setPendingSearchBehaviorChange] =
    useState<PendingSearchBehaviorChange | null>(null)
  const [isIndexingStrategyDialogOpen, setIsIndexingStrategyDialogOpen] = useState(false)
  const persistedVariantOptions = useMemo(() => {
    const parsed = z.array(z.string()).safeParse(capability.config?.availableVariantOptions)
    return parsed.success ? parsed.data : []
  }, [capability.config?.availableVariantOptions])
  const shouldLoadShopifyVariantOptions =
    provider === SearchCatalogProvider.Shopify && !isIntegrationsStatusPending
  const { data: shopifyVariantOptions = [], isFetching: isFetchingVariantOptions } =
    useShopifyVariantOptionsQuery(shouldLoadShopifyVariantOptions)
  const availableVariantOptions = useMemo(
    () => [
      ...new Map(
        [...shopifyVariantOptions, ...persistedVariantOptions, persistedGroupVariantsBy ?? ""]
          .map((option) => option.trim())
          .filter(Boolean)
          .map((option) => [option.toLowerCase(), option] as const),
      ).values(),
    ],
    [persistedGroupVariantsBy, persistedVariantOptions, shopifyVariantOptions],
  )
  const hasAvailableVariantOptions = availableVariantOptions.length > 0

  const productSelectionConfig = useMemo(() => {
    const parsed = ProductSelectionConfigSchema.safeParse(capability.config ?? {})
    return parsed.success ? parsed.data : {}
  }, [capability.config])
  const [visibilityMode, setVisibilityMode] = useState<string>(
    productSelectionConfig.visibility?.mode ?? SearchCatalogVisibilityMode.All,
  )
  const [visibilityTags, setVisibilityTags] = useState(
    productSelectionConfig.visibility?.tags ?? [],
  )
  const [visibilityCollections, setVisibilityCollections] = useState(
    productSelectionConfig.visibility?.collections ?? [],
  )
  const [boost, setBoost] = useState<SearchCatalogBoost>(productSelectionConfig.boost ?? [])
  // Existing catalogs only carry tags and collections once a sync has re-indexed them.
  const isProductSelectionReady = mode === "install" || !isNil(productSelectionConfig.availableTags)
  const shouldLoadProductSelectionOptions =
    shouldLoadShopifyVariantOptions && isProductSelectionReady
  const { data: shopifyTags = [] } = useQuery({
    ...shopifyProductTagsQuery(),
    enabled: shouldLoadProductSelectionOptions,
  })
  const { data: shopifyCollections = [] } = useQuery({
    ...shopifyCollectionsQuery(),
    enabled: shouldLoadProductSelectionOptions,
  })
  const tagOptions = useMemo(() => {
    if (!isProductSelectionReady) return []

    const tags = new Set([...shopifyTags, ...(productSelectionConfig.availableTags ?? [])])

    return [...tags].map((tag) => ({ value: tag, label: tag }))
  }, [isProductSelectionReady, productSelectionConfig.availableTags, shopifyTags])
  const collectionOptions = useMemo(() => {
    if (!isProductSelectionReady) return []

    const titleByHandle = new Map(
      [...(productSelectionConfig.availableCollections ?? []), ...shopifyCollections].map(
        ({ handle, title }) => [handle, title] as const,
      ),
    )

    return [...titleByHandle].map(([value, label]) => ({ value, label }))
  }, [isProductSelectionReady, productSelectionConfig.availableCollections, shopifyCollections])

  const getBoostValues = (type: SearchCatalogBoost[number]["type"]) =>
    boost.filter((entry) => entry.type === type).map((entry) => entry.value)
  const boostTags = getBoostValues(SearchCatalogBoostEntryType.Tag)
  const boostCollections = getBoostValues(SearchCatalogBoostEntryType.Collection)

  const setBoostValues = (type: SearchCatalogBoost[number]["type"]) => (values: string[]) => {
    const kept = boost.filter((entry) => entry.type !== type || values.includes(entry.value))
    const keptValues = new Set(getBoostValues(type))
    const added = values.filter((value) => !keptValues.has(value)).map((value) => ({ type, value }))
    const next = [...kept, ...added]

    if (next.length > SEARCH_CATALOG_MAX_BOOST_ENTRIES) {
      toast.error(
        t("capabilities.catalog.SearchCatalog.config.productSelection.boost.maxReached", {
          count: SEARCH_CATALOG_MAX_BOOST_ENTRIES,
        }),
      )
      return
    }

    setBoost(next)
  }

  const getBoostLabel = (entry: SearchCatalogBoost[number]) =>
    entry.type === SearchCatalogBoostEntryType.Collection
      ? (collectionOptions.find((option) => option.value === entry.value)?.label ?? entry.value)
      : entry.value

  const setUtmField = useCallback(
    (field: keyof SearchCatalogUtmParams) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setUtmParams((prev) => ({ ...prev, [field]: e.target.value }))
    },
    [],
  )

  const buildUtmParams = useCallback(() => {
    return {
      source: "permut",
      medium: "{{ conversation.channel }}",
      campaign: "{{ conversation.campaign.name }}",
      term: utmParams.term.trim() || null,
      content: utmParams.content.trim() || null,
    }
  }, [utmParams])

  const getGroupVariantsByForMode = useCallback(
    (nextMode: string) => {
      if (nextMode !== SearchCatalogEntityIndexingMode.Variant) return null
      const selectedGroupVariantsBy = findVariantOption(availableVariantOptions, groupVariantsBy)
      if (selectedGroupVariantsBy) return selectedGroupVariantsBy

      const selectedPersistedGroupVariantsBy = findVariantOption(
        availableVariantOptions,
        persistedGroupVariantsBy,
      )
      if (selectedPersistedGroupVariantsBy) return selectedPersistedGroupVariantsBy

      return availableVariantOptions.length === 1 ? (availableVariantOptions[0] ?? null) : null
    },
    [availableVariantOptions, groupVariantsBy, persistedGroupVariantsBy],
  )

  const applySearchBehaviorChange = useCallback((change: PendingSearchBehaviorChange) => {
    setEntityIndexingMode(change.entityIndexingMode)
    setGroupVariantsBy(change.groupVariantsBy)
  }, [])

  const requestSearchBehaviorChange = useCallback(
    (change: PendingSearchBehaviorChange) => {
      if (
        change.entityIndexingMode === entityIndexingMode &&
        areVariantOptionsEqual(change.groupVariantsBy, groupVariantsBy)
      ) {
        return
      }

      const matchesPersistedConfig =
        change.entityIndexingMode === persistedEntityIndexingMode &&
        areVariantOptionsEqual(change.groupVariantsBy, persistedGroupVariantsBy)

      const previousVariantByWasNotDefined = isNil(groupVariantsBy)

      if (mode !== "update" || matchesPersistedConfig || previousVariantByWasNotDefined) {
        applySearchBehaviorChange(change)
        return
      }

      setPendingSearchBehaviorChange(change)
      setIsIndexingStrategyDialogOpen(true)
    },
    [
      applySearchBehaviorChange,
      entityIndexingMode,
      groupVariantsBy,
      mode,
      persistedEntityIndexingMode,
      persistedGroupVariantsBy,
    ],
  )

  const handleEntityIndexingModeChange = useCallback(
    (nextMode: string) => {
      requestSearchBehaviorChange({
        entityIndexingMode: nextMode,
        groupVariantsBy: getGroupVariantsByForMode(nextMode),
      })
    },
    [getGroupVariantsByForMode, requestSearchBehaviorChange],
  )

  const handleGroupVariantsByChange = useCallback(
    (nextGroupVariantsBy: string) => {
      requestSearchBehaviorChange({
        entityIndexingMode,
        groupVariantsBy: nextGroupVariantsBy,
      })
    },
    [entityIndexingMode, requestSearchBehaviorChange],
  )

  const handleConfirmSearchBehaviorChange = useCallback(() => {
    if (pendingSearchBehaviorChange) {
      applySearchBehaviorChange(pendingSearchBehaviorChange)
    }

    setPendingSearchBehaviorChange(null)
    setIsIndexingStrategyDialogOpen(false)
  }, [applySearchBehaviorChange, pendingSearchBehaviorChange])

  const handleCancelSearchBehaviorChange = useCallback(() => {
    setPendingSearchBehaviorChange(null)
    setIsIndexingStrategyDialogOpen(false)
  }, [])

  useEffect(() => {
    if (entityIndexingMode !== SearchCatalogEntityIndexingMode.Variant) return
    if (groupVariantsBy || availableVariantOptions.length !== 1) return

    setGroupVariantsBy(availableVariantOptions[0] ?? null)
  }, [availableVariantOptions, entityIndexingMode, groupVariantsBy])

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return
      const selectedGroupVariantsBy = groupVariantsBy?.trim() || null
      const availableSelectedGroupVariantsBy = findVariantOption(
        availableVariantOptions,
        selectedGroupVariantsBy,
      )
      const selectedGroupIsUnavailable =
        !!selectedGroupVariantsBy && hasAvailableVariantOptions && !availableSelectedGroupVariantsBy

      if (
        entityIndexingMode === SearchCatalogEntityIndexingMode.Variant &&
        (!selectedGroupVariantsBy || selectedGroupIsUnavailable)
      ) {
        toast.error(
          t("capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsByRequired"),
        )
        return
      }

      const isVisibilityRestricted = visibilityMode !== SearchCatalogVisibilityMode.All

      if (
        isVisibilityRestricted &&
        visibilityTags.length === 0 &&
        visibilityCollections.length === 0
      ) {
        toast.error(
          t("capabilities.catalog.SearchCatalog.config.productSelection.visibility.required"),
        )
        return
      }

      const config = {
        provider,
        utmParams: buildUtmParams(),
        entityIndexingMode,
        groupVariantsBy:
          entityIndexingMode === SearchCatalogEntityIndexingMode.Variant
            ? (availableSelectedGroupVariantsBy ?? selectedGroupVariantsBy)
            : null,
        availabilityFilter,
        urlType,
        visibility: {
          mode: visibilityMode,
          tags: isVisibilityRestricted ? visibilityTags : [],
          collections: isVisibilityRestricted ? visibilityCollections : [],
        },
        boost,
      }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config,
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      availabilityFilter,
      boost,
      buildUtmParams,
      capability.id,
      capability.identifier,
      availableVariantOptions,
      entityIndexingMode,
      groupVariantsBy,
      hasAvailableVariantOptions,
      installCapability,
      isPending,
      mode,
      onClose,
      onConfigure,
      provider,
      t,
      updateCapabilityConfig,
      urlType,
      visibilityCollections,
      visibilityMode,
      visibilityTags,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending) return <Loading />

  const lastSyncedAt = mode === "update" ? (capability.config?.lastSyncedAt as string | null) : null
  const productsCount =
    mode === "update" ? (capability.config?.productsCount as number | null) : null
  const isGroupVariantsBySelectDisabled = !hasAvailableVariantOptions

  function getSearchBehaviorCopy() {
    if (isFetchingVariantOptions && !hasAvailableVariantOptions) {
      return t("capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsByLoading")
    }

    if (hasAvailableVariantOptions) {
      return t("capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsByHint")
    }

    return t("capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsByEmpty")
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Label>{t(`capabilities.catalog.${capability.identifier}.config.provider`)}</Label>
        <Select value={provider ?? undefined} onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue
              placeholder={t(`capabilities.catalog.${capability.identifier}.config.placeholder`)}
            />
          </SelectTrigger>
          <SelectContent>
            {providerOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {mode === "install" && (
        <Alert variant="warning">
          <ClockIcon className="size-4" />
          <AlertTitle>{t("capabilities.catalog.SearchCatalog.config.syncWarning")}</AlertTitle>
          <AlertDescription>
            {t("capabilities.catalog.SearchCatalog.config.syncWarningDescription")}
          </AlertDescription>
        </Alert>
      )}
      {mode === "update" && (
        <div className="flex gap-6 rounded-lg border bg-background px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              {t("capabilities.catalog.SearchCatalog.config.lastSyncedAt")}
            </span>
            <span className="text-sm">
              {lastSyncedAt ? (
                <DateDisplay date={lastSyncedAt} />
              ) : (
                t("capabilities.catalog.SearchCatalog.config.never")
              )}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              {t("capabilities.catalog.SearchCatalog.config.productsCount")}
            </span>
            <span className="text-sm">{productsCount ?? "-"}</span>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <div className="flex flex-col gap-1.5">
          <Label>
            {t("capabilities.catalog.SearchCatalog.config.searchBehavior.availabilityFilter")}
          </Label>
          <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  "capabilities.catalog.SearchCatalog.config.searchBehavior.availabilityFilterPlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {Object.values(SearchCatalogAvailabilityFilter).map((filter) => (
                <SelectItem key={filter} value={filter}>
                  {t(
                    `capabilities.catalog.SearchCatalog.config.searchBehavior.availabilityFilterOptions.${filter}`,
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.SearchCatalog.config.searchBehavior.availabilityFilterHints.${availabilityFilter}`,
            )}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t("capabilities.catalog.SearchCatalog.config.searchBehavior.urlType")}</Label>
          <Select value={urlType} onValueChange={setUrlType}>
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  "capabilities.catalog.SearchCatalog.config.searchBehavior.urlTypePlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {Object.values(SearchCatalogUrlType).map((type) => (
                <SelectItem key={type} value={type}>
                  {t(
                    `capabilities.catalog.SearchCatalog.config.searchBehavior.urlTypeOptions.${type}`,
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t(`capabilities.catalog.SearchCatalog.config.searchBehavior.urlTypeHints.${urlType}`)}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <div className="flex flex-col gap-1.5">
          <Label>
            {t("capabilities.catalog.SearchCatalog.config.productSelection.visibility.mode")}
          </Label>
          <Select value={visibilityMode} onValueChange={setVisibilityMode}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(SearchCatalogVisibilityMode).map((option) => (
                <SelectItem key={option} value={option}>
                  {t(
                    `capabilities.catalog.SearchCatalog.config.productSelection.visibility.modeOptions.${option}`,
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.SearchCatalog.config.productSelection.visibility.modeHints.${visibilityMode}`,
            )}
          </p>
        </div>
        {visibilityMode !== SearchCatalogVisibilityMode.All && (
          <div className="grid grid-cols-2 gap-2">
            <CatalogMultiSelect
              label={t("capabilities.catalog.SearchCatalog.config.productSelection.tags")}
              placeholder={t(
                "capabilities.catalog.SearchCatalog.config.productSelection.tagsPlaceholder",
              )}
              options={mergeSelectedOptions(tagOptions, visibilityTags)}
              selectedValues={visibilityTags}
              onChange={setVisibilityTags}
            />
            <CatalogMultiSelect
              label={t("capabilities.catalog.SearchCatalog.config.productSelection.collections")}
              placeholder={t(
                "capabilities.catalog.SearchCatalog.config.productSelection.collectionsPlaceholder",
              )}
              options={mergeSelectedOptions(collectionOptions, visibilityCollections)}
              selectedValues={visibilityCollections}
              onChange={setVisibilityCollections}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <div className="flex flex-col gap-1.5">
          <Label>
            {t("capabilities.catalog.SearchCatalog.config.productSelection.boost.title")}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t("capabilities.catalog.SearchCatalog.config.productSelection.boost.hint", {
              count: SEARCH_CATALOG_MAX_BOOST_ENTRIES,
            })}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <CatalogMultiSelect
            label={t("capabilities.catalog.SearchCatalog.config.productSelection.tags")}
            placeholder={t(
              "capabilities.catalog.SearchCatalog.config.productSelection.tagsPlaceholder",
            )}
            options={mergeSelectedOptions(tagOptions, boostTags)}
            selectedValues={boostTags}
            onChange={setBoostValues(SearchCatalogBoostEntryType.Tag)}
          />
          <CatalogMultiSelect
            label={t("capabilities.catalog.SearchCatalog.config.productSelection.collections")}
            placeholder={t(
              "capabilities.catalog.SearchCatalog.config.productSelection.collectionsPlaceholder",
            )}
            options={mergeSelectedOptions(collectionOptions, boostCollections)}
            selectedValues={boostCollections}
            onChange={setBoostValues(SearchCatalogBoostEntryType.Collection)}
          />
        </div>
        {boost.length > 0 && (
          <>
            <CatalogBoostList entries={boost} getLabel={getBoostLabel} onChange={setBoost} />
            <p className="text-sm text-muted-foreground">
              {t("capabilities.catalog.SearchCatalog.config.productSelection.boost.priorityHint")}
            </p>
          </>
        )}
        {!isProductSelectionReady && (
          <p className="text-sm text-muted-foreground">
            {t("capabilities.catalog.SearchCatalog.config.productSelection.empty")}
          </p>
        )}
      </div>
      <Accordion type="single" collapsible className="border rounded-lg bg-background">
        <AccordionItem value="utmParams">
          <AccordionTrigger>
            <div className="flex flex-col items-start gap-1">
              <span className="text-base font-medium">
                {t("capabilities.catalog.SearchCatalog.config.utmParams.title")}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {t("capabilities.catalog.SearchCatalog.config.utmParams.description")}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-2 pb-4">
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ["source", "sourcePlaceholder"],
                  ["medium", "mediumPlaceholder"],
                  ["campaign", "campaignPlaceholder"],
                  ["term", "termPlaceholder"],
                  ["content", "contentPlaceholder"],
                ] as const
              ).map(([field, placeholderKey]) => (
                <div key={field} className="flex flex-col gap-0.5">
                  <Label className="text-xs">
                    {t(`capabilities.catalog.SearchCatalog.config.utmParams.${field}`)}
                  </Label>
                  <Input
                    value={utmParams[field]}
                    onChange={setUtmField(field)}
                    disabled={field === "source" || field === "medium" || field === "campaign"}
                    placeholder={t(
                      `capabilities.catalog.SearchCatalog.config.utmParams.${placeholderKey}`,
                    )}
                  />
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <Accordion type="single" collapsible className="border rounded-lg bg-background">
        <AccordionItem value="indexingStrategy">
          <AccordionTrigger>
            <div className="flex flex-col items-start gap-1">
              <span className="text-base font-medium">
                {t("capabilities.catalog.SearchCatalog.config.searchBehavior.indexingStrategy")}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {t(
                  "capabilities.catalog.SearchCatalog.config.searchBehavior.indexingStrategyDescription",
                )}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3 pb-4">
            <div className="flex flex-col gap-1.5">
              <Select value={entityIndexingMode} onValueChange={handleEntityIndexingModeChange}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "capabilities.catalog.SearchCatalog.config.searchBehavior.indexingStrategyPlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(SearchCatalogEntityIndexingMode).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {t(
                        `capabilities.catalog.SearchCatalog.config.searchBehavior.indexingStrategyOptions.${mode}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t(
                  `capabilities.catalog.SearchCatalog.config.searchBehavior.indexingStrategyHints.${entityIndexingMode}`,
                )}
              </p>
            </div>
            {entityIndexingMode === SearchCatalogEntityIndexingMode.Variant && (
              <div className="flex flex-col gap-1.5">
                <Label>
                  {t("capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsBy")}
                </Label>
                <Select
                  value={
                    findVariantOption(availableVariantOptions, groupVariantsBy) ??
                    groupVariantsBy ??
                    undefined
                  }
                  onValueChange={handleGroupVariantsByChange}
                  disabled={isGroupVariantsBySelectDisabled}
                >
                  <SelectTrigger disabled={isGroupVariantsBySelectDisabled}>
                    <SelectValue
                      placeholder={t(
                        "capabilities.catalog.SearchCatalog.config.searchBehavior.groupVariantsByPlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVariantOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">{getSearchBehaviorCopy()}</p>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <Dialog
        open={isIndexingStrategyDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelSearchBehaviorChange()
          }
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {t("capabilities.catalog.SearchCatalog.config.indexingStrategyDialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("capabilities.catalog.SearchCatalog.config.indexingStrategyDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelSearchBehaviorChange}>
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleConfirmSearchBehaviorChange}>{t("shared.continue")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
